import React, { useState, useEffect } from 'react';
import { settingsAPI } from '../services/api';
import toast from 'react-hot-toast';
import { Save, ChevronUp, ChevronDown, Eye, EyeOff, Smartphone } from 'lucide-react';
import { flatNavItems } from '../components/common/navConfig';

const MAX_BOTTOM_TABS = 5;
// 'customers' (not '/customers') — the combined CableTV/Broadband picker entry is
// identified by its navConfig key, since its `to` is just its default/fallback route.
const DEFAULT_BOTTOM_TABS = ['/dashboard', 'customers', '/payments', '/renewals'];

const move = (arr, idx, dir) => {
  const next = [...arr];
  const swap = idx + dir;
  if (swap < 0 || swap >= next.length) return arr;
  [next[idx], next[swap]] = [next[swap], next[idx]];
  return next;
};

const rowStyle = {
  display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
  border: '1px solid var(--border)', borderRadius: 8, marginBottom: 6, background: 'var(--bg)',
};
const iconBtnStyle = { padding: '3px 7px' };

// Lets an admin choose which nav items get a permanent bottom tab in the app/tablet
// view, and reorder/hide items in that view's "More" sheet — everything else about
// the nav (routes, labels, sections) still comes from navConfig; this only decides
// placement within the app-view shell. Doesn't touch the web sidebar at all.
export default function AppViewSettingsTab() {
  const [bottomTabs, setBottomTabs] = useState(DEFAULT_BOTTOM_TABS);
  const [order, setOrder] = useState(flatNavItems.map(i => i.id));
  const [hidden, setHidden] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    settingsAPI.getAppView()
      .then(r => {
        const d = r.data.data || {};
        setBottomTabs(d.bottomTabs?.length ? d.bottomTabs : DEFAULT_BOTTOM_TABS);
        const knownIds = flatNavItems.map(i => i.id);
        const saved = (d.moreOrder || []).filter(id => knownIds.includes(id));
        const missing = knownIds.filter(id => !saved.includes(id));
        setOrder([...saved, ...missing]);
        setHidden(d.moreHidden || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const findItem = (id) => flatNavItems.find(i => i.id === id);

  const toggleBottomTab = (id) => {
    setBottomTabs(t => {
      if (t.includes(id)) return t.filter(x => x !== id);
      if (t.length >= MAX_BOTTOM_TABS) {
        toast.error(`Bottom bar holds ${MAX_BOTTOM_TABS} tabs max (plus "More")`);
        return t;
      }
      return [...t, id];
    });
  };

  const toggleHidden = (id) => setHidden(h => (h.includes(id) ? h.filter(x => x !== id) : [...h, id]));

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsAPI.saveAppView({ bottomTabs, moreOrder: order, moreHidden: hidden });
      toast.success('App View settings saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
      <div className="spinner" style={{ margin: '0 auto 12px' }} />Loading…
    </div>
  );

  return (
    <div className="card"><div className="card-body">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <Smartphone size={16} />
        <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>App / Tablet View Layout</h3>
      </div>
      <p style={{ color: 'var(--text-muted)', fontSize: '.83rem', marginTop: 0, marginBottom: 24 }}>
        Controls the bottom-nav shell shown on phones/tablets (or whenever "App View" is switched on manually).
        Has no effect on the web sidebar. "Customers" opens a CableTV/Broadband picker rather than an unfiltered
        list — pick "CableTV" or "Broadband" directly here instead if you'd rather skip straight to one of them.
      </p>

      {/* ── Bottom Tab Bar ── */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ fontSize: '.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 10 }}>
          Bottom Tab Bar ({bottomTabs.length}/{MAX_BOTTOM_TABS} + More)
        </div>

        {bottomTabs.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            {bottomTabs.map((id, idx) => {
              const item = findItem(id);
              if (!item) return null;
              const Icon = item.icon;
              return (
                <div key={id} style={rowStyle}>
                  <Icon size={16} />
                  <span style={{ flex: 1, fontSize: '.85rem', fontWeight: 600 }}>{item.label}</span>
                  <button type="button" onClick={() => setBottomTabs(t => move(t, idx, -1))} disabled={idx === 0} className="btn btn-secondary btn-sm" style={iconBtnStyle}><ChevronUp size={13} /></button>
                  <button type="button" onClick={() => setBottomTabs(t => move(t, idx, 1))} disabled={idx === bottomTabs.length - 1} className="btn btn-secondary btn-sm" style={iconBtnStyle}><ChevronDown size={13} /></button>
                  <button type="button" onClick={() => toggleBottomTab(id)} className="btn btn-secondary btn-sm">Remove</button>
                </div>
              );
            })}
          </div>
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {flatNavItems.filter(i => !bottomTabs.includes(i.id)).map(item => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => toggleBottomTab(item.id)}
                className="btn btn-secondary btn-sm"
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <Icon size={13} /> {item.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── More Menu order / visibility ── */}
      <div>
        <div style={{ fontSize: '.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 10 }}>
          "More" Menu Order
        </div>
        {order.map((id, idx) => {
          const item = findItem(id);
          if (!item) return null;
          const Icon = item.icon;
          const isHidden = hidden.includes(id);
          return (
            <div key={id} style={{ ...rowStyle, opacity: isHidden ? .5 : 1 }}>
              <Icon size={16} />
              <span style={{ flex: 1, fontSize: '.85rem' }}>{item.label}</span>
              <button type="button" onClick={() => setOrder(o => move(o, idx, -1))} disabled={idx === 0} className="btn btn-secondary btn-sm" style={iconBtnStyle}><ChevronUp size={13} /></button>
              <button type="button" onClick={() => setOrder(o => move(o, idx, 1))} disabled={idx === order.length - 1} className="btn btn-secondary btn-sm" style={iconBtnStyle}><ChevronDown size={13} /></button>
              <button
                type="button"
                onClick={() => toggleHidden(id)}
                className="btn btn-secondary btn-sm"
                title={isHidden ? 'Show in More menu' : 'Hide from More menu'}
              >
                {isHidden ? <EyeOff size={13} /> : <Eye size={13} />}
              </button>
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          <Save size={14} style={{ marginRight: 6 }} />
          {saving ? 'Saving…' : 'Save App View Settings'}
        </button>
      </div>
    </div></div>
  );
}
